/**
 * DotRule — a decorative horizontal rule made of circles that
 * diminish in size from left to right until they vanish.
 * Sits inside a `.container` and respects page margins.
 */

import './DotRule.css';

interface Props {
  /** Number of dots in the row */
  count?: number;
  /** Radius of the largest (first) dot in px */
  maxRadius?: number;
  /** Edge-to-edge gap between dots in px */
  gap?: number;
  className?: string;
}

export function DotRule({ count = 50, maxRadius = 10, gap = 16, className }: Props) {
  const radii = Array.from({ length: count }, (_, i) => {
    const t = i / (count - 1);
    return maxRadius * Math.pow(1 - t, 1.2);
  });

  // Position dots with equal edge-to-edge gaps, in real pixels.
  const positions: number[] = [];
  let x = radii[0];
  for (let i = 0; i < count; i++) {
    positions.push(x);
    if (i < count - 1) {
      x += radii[i] + gap + radii[i + 1];
    }
  }
  const totalWidth = x + radii[count - 1];
  const height = maxRadius * 2;

  return (
    <svg
      className={`dot-rule ${className ?? ''}`}
      width={totalWidth}
      height={height}
      viewBox={`0 0 ${totalWidth} ${height}`}
      aria-hidden="true"
    >
      {radii.map((r, i) => (
        <circle
          key={i}
          cx={positions[i]}
          cy={maxRadius}
          r={r}
          fill="currentColor"
        />
      ))}
    </svg>
  );
}
